import { Message } from '../types';
import { logger, type LogLevel } from './logger';

export interface CapturedLog {
  timestamp: number;
  level: 'info' | 'error' | 'warn' | 'debug';
  message: string;
  type?: 'explanation' | 'query' | 'general';
  data?: Record<string, any>;
}

/**
 * Log level hierarchy for filtering
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  errors: 0,
  warnings: 1,
  info: 2,
  verbose: 3,
};

const MESSAGE_LEVEL_PRIORITY: Record<'error' | 'warn' | 'info' | 'debug', number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

/**
 * UILogCollector captures logs during user prompt processing
 * and sends them to runtime via ui_logs message with uiBlockId as the message id
 * Logs are sent in real-time for streaming effect in the UI
 * Respects the global log level configuration
 */
export class UILogCollector {
  private logs: CapturedLog[] = [];
  private uiBlockId: string | null;
  private clientId: string;
  private sendMessage: (message: Message) => void;
  private currentLogLevel: LogLevel;

  constructor(
    clientId: string,
    sendMessage: (message: Message) => void,
    uiBlockId?: string
  ) {
    this.uiBlockId = uiBlockId || null;
    this.clientId = clientId;
    this.sendMessage = sendMessage;
    this.currentLogLevel = logger.getLogLevel();
  }

  /**
   * Check if logging is enabled (uiBlockId is provided)
   */
  isEnabled(): boolean {
    return this.uiBlockId !== null;
  }

  /**
   * Check if a message should be logged based on current log level
   */
  private shouldLog(messageLevel: 'error' | 'warn' | 'info' | 'debug'): boolean {
    const currentLevelPriority = LOG_LEVEL_PRIORITY[this.currentLogLevel];
    const messagePriority = MESSAGE_LEVEL_PRIORITY[messageLevel];
    return messagePriority <= currentLevelPriority;
  }

  /**
   * Add a log entry with timestamp and immediately send to runtime
   * Only logs that pass the log level filter are captured and sent
   */
  private addLog(
    level: 'info' | 'error' | 'warn' | 'debug',
    message: string,
    type?: 'explanation' | 'query' | 'general',
    data?: Record<string, any>
  ): void {
    // Check if this log level should be captured based on current log level
    if (!this.shouldLog(level)) {
      return;
    }

    const log: CapturedLog = {
      timestamp: Date.now(),
      level,
      message,
      ...(type && { type }),
      ...(data && { data }),
    };

    this.logs.push(log);

    // Send the log immediately to runtime for streaming effect
    this.sendLogImmediately(log);

    // Also log to terminal using the main logger based on level
    switch (level) {
      case 'error':
        logger.error('UILogCollector:', log);
        break;
      case 'warn':
        logger.warn('UILogCollector:', log);
        break;
      case 'info':
        logger.info('UILogCollector:', log);
        break;
      case 'debug':
        logger.debug('UILogCollector:', log);
        break;
    }
  }

  /**
   * Send a single log to runtime immediately
   */
  private sendLogImmediately(log: CapturedLog): void {
    if (!this.isEnabled()) {
      return;
    }

    const response: Message = {
      id: this.uiBlockId!,
      type: 'UI_LOGS',
      from: { type: 'data-agent' },
      to: {
        type: 'runtime',
        id: this.clientId,
      },
      payload: {
        logs: [log], // Send single log in array
      },
    };

    this.sendMessage(response);
  }

  /**
   * Log info message
   */
  info(message: string, type?: 'explanation' | 'query' | 'general', data?: Record<string, any>): void {
    if (this.isEnabled()) {
      this.addLog('info', message, type, data);
    }
  }

  /**
   * Log error message
   */
  error(message: string, type?: 'explanation' | 'query' | 'general', data?: Record<string, any>): void {
    if (this.isEnabled()) {
      this.addLog('error', message, type, data);
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, type?: 'explanation' | 'query' | 'general', data?: Record<string, any>): void {
    if (this.isEnabled()) {
      this.addLog('warn', message, type, data);
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, type?: 'explanation' | 'query' | 'general', data?: Record<string, any>): void {
    if (this.isEnabled()) {
      this.addLog('debug', message, type, data);
    }
  }

  /**
   * Log LLM explanation with typed metadata
   */
  logExplanation(message: string, explanation: string, data?: Record<string, any>): void {
    if (this.isEnabled()) {
      this.addLog('info', message, 'explanation', {
        explanation,
        ...data,
      });
    }
  }

  /**
   * Log generated query with typed metadata
   */
  logQuery(message: string, query: string, data?: Record<string, any>): void {
    if (this.isEnabled()) {
      this.addLog('info', message, 'query', {
        query,
        ...data,
      });
    }
  }

  /**
   * Send all collected logs at once (optional, for final summary)
   */
  sendAllLogs(): void {
    if (!this.isEnabled() || this.logs.length === 0) {
      return;
    }

    const response: Message = {
      id: this.uiBlockId!,
      type: 'UI_LOGS',
      from: { type: 'data-agent' },
      to: {
        type: 'runtime',
        id: this.clientId,
      },
      payload: {
        logs: this.logs,
      },
    };

    this.sendMessage(response);
  }

  /**
   * Get all collected logs
   */
  getLogs(): CapturedLog[] {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Set uiBlockId (in case it's provided later)
   */
  setUIBlockId(uiBlockId: string): void {
    this.uiBlockId = uiBlockId;
  }
}
